import { makeAutoObservable } from 'mobx'
import { debounce } from 'lodash'
import { AxiosError } from 'axios'
import { isEmail, logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import { type IReferralsHistory, ReferralsApi, Referrals } from 'entities/Referrals'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export class ReferralsViewStore {
  private _isInitializing = false
  private _history: IReferralsHistory | null = null
  private _email = ''
  private _emailLocalError = ''
  private _isEmailVerified = false
  private _isEmailSaved = false
  private _isEditMode = false
  private _savedEmail: null | string = null
  private _loading = false
  private _debounceCheckEmail

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._debounceCheckEmail = debounce(this.checkIsEmail, 1000)
  }

  setLoading = (status: boolean) => {
    this._loading = status
  }

  loadReferralsViewInitialData = async () => {
    this._isInitializing = true

    try {
      const { data } = await ReferralsApi.getReferralsHistory()

      if (data) {
        this._history = new Referrals(data)

        if (this._history.paypalEmail) {
          this._email = this._history.paypalEmail
          this._savedEmail = this._email
          this._isEmailSaved = true
        }
      }

      if (!this._isEmailSaved) this._isEditMode = true
    } catch (error) {
      logger.error(error)
    } finally {
      this._isInitializing = false
    }
  }

  createReferrals = async () => {
    this._isInitializing = true
    try {
      const { data } = await ReferralsApi.createReferrals()

      if (data.first_promoter_ref_id) {
        this._entities.usersStore.user?.updateFirstPromoterRefId(data.first_promoter_ref_id)

        await this.loadReferralsViewInitialData()
      }
    } catch (error) {
      logger.error(error)
      this._isInitializing = false
    }
  }

  setEmailLocalError = (error: string) => {
    this._emailLocalError = error
  }

  setEmail = (email: string) => {
    this.setEmailLocalError('')
    this._isEmailVerified = false

    if (email) {
      this._debounceCheckEmail()
    }

    this._email = email
  }

  checkIsEmail = () => {
    if (this.isEmptyEmail) return

    if (this._email === this._savedEmail) {
      this._isEmailSaved = true
      this._isEditMode = false
      return
    }

    const isCorrect = isEmail(this._email)

    if (isCorrect) {
      this.setEmailLocalError('')
      this._isEmailVerified = true
      return
    }

    this._isEmailVerified = false
    this.setEmailLocalError('Please enter a valid email address')
  }

  onSave = async () => {
    if (this.isEmptyEmail || !this.isEmailVerified) return

    this.setLoading(true)
    try {
      const { data } = await ReferralsApi.setPaypalEmail({ paypal_email: this._email })

      if (data) {
        this._isEmailSaved = true
        this._savedEmail = this._email

        showToast({
          type: 'success',
          title: 'PayPal email saved',
        })
      }
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        showToast({
          type: 'error',
          title:
            e.errors.getError('error') ||
            e.errors.getError('default') ||
            'Something went wrong, please try again.',
        })
      }
    } finally {
      this.setLoading(false)
      this._isEditMode = false
    }
  }

  onDelete = async () => {
    try {
      this.setLoading(true)

      showToast({
        type: 'info',
        title: 'PayPal email removed',
      })
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        showToast({
          type: 'error',
          title:
            e.errors.getError('error') ||
            e.errors.getError('default') ||
            'Something went wrong, please try again.',
        })
      }
    } finally {
      this.setLoading(false)
    }
  }

  setEditMode = () => {
    this._isEditMode = true
  }

  get isEditMode() {
    return this._isEditMode
  }

  get isBaseDataLoading() {
    return this._isInitializing
  }

  get isPageSpinnerShown() {
    return this.isBaseDataLoading
  }

  get referralsInfo() {
    return this._history
  }

  get email() {
    return this._email
  }

  get savedEmail() {
    return this._savedEmail
  }

  get isEmptyEmail() {
    return !this._email
  }

  get emailLocalError() {
    return this._emailLocalError
  }

  get isEmailVerified() {
    return this._isEmailVerified
  }

  get isEmailSaved() {
    return this._isEmailSaved
  }

  get showSaveButton() {
    return this._isEditMode || !this._savedEmail
  }

  get saveDisabled() {
    return this.isEmptyEmail || !this.isEmailVerified
  }

  get loading() {
    return this._loading
  }

  dispose = () => {
    this._debounceCheckEmail.cancel()
  }
}
